/**
 * SportsTeamPicker — inline team picker inside the sports tile detail sheet.
 *
 * D-011: team picker lives inline in the tile's detail sheet, not a separate
 * settings screen (core-flows §4.3).
 *
 * Design:
 *   1. League select (EPL / NFL / NBA / MLB / NHL).
 *   2. Popular teams list per league (curated chips with REAL TheSportsDB numeric
 *      ids — sourced from https://www.thesportsdb.com/api/v1/json/3/searchteams.php
 *      on 2026-07-24; ids verified by direct API calls).
 *   3. Free-text teamId fallback (validated by sportsParamsSchema.teamId pattern:
 *      /^[a-zA-Z0-9_-]{1,32}$/).
 *   4. Save button commits through onSave → commitConfigure → tile_configured.
 *
 * Curated team list source (verified 2026-07-24):
 *   https://www.thesportsdb.com/api/v1/json/3/searchteams.php?t={teamName}
 *
 * Immutability: functional component, no prop mutation.
 * File size: ~200 lines.
 */
import React, { useState } from 'react';
import { SPORTS_LEAGUES } from 'briefpane/schema';

/* -------------------------------------------------------------------------- */
/* Curated popular teams per league (TheSportsDB numeric ids, verified live)  */
/* Source: https://www.thesportsdb.com/api/v1/json/3/searchteams.php?t=<name>  */
/* Date verified: 2026-07-24                                                   */
/* -------------------------------------------------------------------------- */

interface PopularTeam {
  readonly id: string;   // TheSportsDB numeric team id (as string for params)
  readonly name: string; // Display name
}

const POPULAR_TEAMS: Record<string, readonly PopularTeam[]> = {
  epl: [
    { id: '133604', name: 'Arsenal' },
    { id: '133602', name: 'Liverpool' },
    { id: '133613', name: 'Manchester City' },
    { id: '133612', name: 'Manchester United' },
    { id: '133610', name: 'Chelsea' },
  ],
  nfl: [
    { id: '134931', name: 'Kansas City Chiefs' },
    { id: '134934', name: 'Dallas Cowboys' },
    { id: '134940', name: 'Green Bay Packers' },
    { id: '134918', name: 'Buffalo Bills' },
    { id: '134922', name: 'Baltimore Ravens' },
  ],
  nba: [
    { id: '134867', name: 'LA Lakers' },
    { id: '134865', name: 'Golden State Warriors' },
    { id: '134860', name: 'Boston Celtics' },
    { id: '134861', name: 'Brooklyn Nets' },
    { id: '134870', name: 'Chicago Bulls' },
  ],
  mlb: [
    { id: '135260', name: 'NY Yankees' },
    { id: '135272', name: 'LA Dodgers' },
    { id: '135279', name: 'SF Giants' },
    { id: '135252', name: 'Boston Red Sox' },
    { id: '135269', name: 'Chicago Cubs' },
  ],
  nhl: [
    { id: '134837', name: 'Toronto Maple Leafs' },
    { id: '134830', name: 'Boston Bruins' },
    { id: '134844', name: 'Pittsburgh Penguins' },
    { id: '134832', name: 'Detroit Red Wings' },
    { id: '134854', name: 'Chicago Blackhawks' },
  ],
};

const LEAGUE_LABELS: Record<string, string> = {
  epl: 'Premier League (EPL)',
  nfl: 'NFL',
  nba: 'NBA',
  mlb: 'MLB',
  nhl: 'NHL',
};

/** TheSportsDB teamId pattern (mirrors sportsParamsSchema.teamId regex). */
const TEAM_ID_PATTERN = /^[a-zA-Z0-9_-]{1,32}$/;

/* -------------------------------------------------------------------------- */
/* Props                                                                       */
/* -------------------------------------------------------------------------- */

export interface SportsTeamPickerProps {
  /** Currently configured league (used to pre-select league tab). */
  readonly currentLeague: string;
  /** Currently configured teamId (empty string if not set). */
  readonly currentTeamId: string;
  /** Called when the user confirms their selection. */
  readonly onSave: (league: string, teamId: string) => void;
  /** If true, the league select is auto-focused on mount. */
  readonly autoFocus?: boolean;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

export function SportsTeamPicker({
  currentLeague,
  currentTeamId,
  onSave,
  autoFocus = false,
}: SportsTeamPickerProps): React.ReactElement {
  const [league, setLeague] = useState<string>(currentLeague);
  const [teamId, setTeamId] = useState<string>(currentTeamId);
  const [customId, setCustomId] = useState<string>('');
  const [useCustom, setUseCustom] = useState(false);
  const [idError, setIdError] = useState<string>('');

  const popularTeams = POPULAR_TEAMS[league] ?? [];

  function handleLeagueChange(newLeague: string): void {
    setLeague(newLeague);
    // Clear team selection when changing league
    setTeamId('');
    setCustomId('');
    setUseCustom(false);
    setIdError('');
  }

  function handleTeamChipClick(id: string): void {
    setTeamId(id);
    setUseCustom(false);
    setCustomId('');
    setIdError('');
  }

  function handleCustomIdChange(value: string): void {
    setCustomId(value);
    setTeamId('');
    setIdError('');
  }

  function handleSave(): void {
    const resolvedTeamId = useCustom ? customId.trim() : teamId;
    if (!resolvedTeamId) {
      setIdError('Please select a team or enter a team ID.');
      return;
    }
    if (!TEAM_ID_PATTERN.test(resolvedTeamId)) {
      setIdError('Team ID must be 1–32 alphanumeric characters (no spaces).');
      return;
    }
    onSave(league, resolvedTeamId);
  }

  const canSave = useCustom
    ? (customId.trim().length > 0 && TEAM_ID_PATTERN.test(customId.trim()))
    : teamId.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <p style={{ fontSize: 'var(--text-detail)', fontWeight: 500, color: 'var(--color-text-primary)', margin: 0 }}>
        Which team do you follow?
      </p>

      {/* League select */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <label
          htmlFor="sports-league-select"
          style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', fontWeight: 500 }}
        >
          League
        </label>
        <select
          id="sports-league-select"
          value={league}
          onChange={(e) => handleLeagueChange(e.target.value)}
          autoFocus={autoFocus}
          style={{
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-delta)',
            color: 'var(--color-text-primary)',
            background: 'var(--color-bg-input)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-button)',
            cursor: 'pointer',
          }}
        >
          {SPORTS_LEAGUES.map((l) => (
            <option key={l} value={l}>{LEAGUE_LABELS[l] ?? l.toUpperCase()}</option>
          ))}
        </select>
      </div>

      {/* Popular teams chips */}
      {popularTeams.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', fontWeight: 500 }}>
            Popular
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {popularTeams.map((team) => {
              const isSelected = !useCustom && teamId === team.id;
              return (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => handleTeamChipClick(team.id)}
                  style={{
                    padding: '4px 12px',
                    fontSize: 'var(--text-delta)',
                    fontWeight: isSelected ? 600 : 400,
                    background: isSelected ? 'var(--color-accent)' : 'var(--color-bg-input)',
                    color: isSelected ? '#fff' : 'var(--color-text-secondary)',
                    border: `1px solid ${isSelected ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    lineHeight: 1.5,
                  }}
                >
                  {team.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom teamId fallback */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <button
          type="button"
          onClick={() => { setUseCustom((prev) => !prev); setTeamId(''); }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: 'var(--text-label)',
            color: 'var(--color-accent)',
            textAlign: 'left',
            fontWeight: 500,
          }}
        >
          {useCustom ? '← Back to popular teams' : 'Enter team ID manually →'}
        </button>
        {useCustom && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <input
              type="text"
              value={customId}
              onChange={(e) => handleCustomIdChange(e.target.value)}
              placeholder="TheSportsDB team ID (e.g. 134865)"
              autoFocus
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-delta)',
                color: 'var(--color-text-primary)',
                background: 'var(--color-bg-input)',
                border: `1px solid ${idError ? 'var(--color-negative-accent)' : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-button)',
                boxSizing: 'border-box',
              }}
            />
            {idError && (
              <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-negative-accent)' }}>
                {idError}
              </span>
            )}
            <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)' }}>
              Find your team ID at{' '}
              <a
                href="https://www.thesportsdb.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--color-accent)' }}
              >
                thesportsdb.com
              </a>
            </span>
          </div>
        )}
      </div>

      {/* Save button */}
      <button
        type="button"
        disabled={!canSave}
        onClick={handleSave}
        style={{
          padding: 'var(--space-2) var(--space-4)',
          fontSize: 'var(--text-delta)',
          fontWeight: 600,
          background: canSave ? 'var(--color-accent)' : 'var(--color-border)',
          color: canSave ? '#fff' : 'var(--color-text-dim)',
          border: 'none',
          borderRadius: 'var(--radius-button)',
          cursor: canSave ? 'pointer' : 'default',
          alignSelf: 'flex-start',
        }}
      >
        Save team
      </button>
    </div>
  );
}
